// pages/journeys.tsx
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import StatusBadge from "../components/journeys/StatusBadge";
import {
  faPlus,
  faRoad,
  faUsers,
  faChartLine,
  faCalendarAlt,
  faEllipsisV,
} from "@fortawesome/free-solid-svg-icons";
import { useTeamContext } from "../contexts/TeamContext";
import { useJourneys } from "../hooks/useJourneys";
import { useApi } from "../hooks/useApi";
import { GameType } from "../hooks/useGameTypes";
import { Category } from "../types/games";
import { Journey, PublishedJourneyChannel } from "../types/journey";

import Card from "../components/card";
import Spinner from "../components/spinner";
import { JourneyWizard, WizardState } from "../components/journeys/wizard";
import JourneyCard from "../components/journeys/JourneyCard";
import EmptyState from "../components/journeys/EmptyState";

// Tab type definition
type TabType = "create" | "audience" | "results";

/**
 * Journeys page component that implements a tabbed interface for managing journeys
 * following the email campaign model where users can create templates, deploy them to
 * channels, and view results.
 */
function Journeys() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { teamId } = useTeamContext();
  const api = useApi();
  const [activeTab, setActiveTab] = useState<TabType>("create");
  const [showJourneyCreationForm, setShowJourneyCreationForm] = useState(false);

  // Add wizard state to parent component to persist state between focus changes
  const [wizardState, setWizardState] = useState<WizardState>({
    currentStep: 0,
    journeyInfo: {
      name: "",
      description: "",
      touchpointCount: 1,
    },
    touchpoints: [
      {
        game_type_id: "",
        offset_days: 0,
        offset_hours: 9, // Default to 9 AM
        offset_minutes: 0,
        content_selection_type: "surprise_me",
      },
    ],
    // Add required contentRows property
    contentRows: {},
  });

  // State for categories used in the journey creation form
  const [categories, setCategories] = useState<Category[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // State for game types
  const [gameTypes, setGameTypes] = useState<GameType[]>([]);
  const [gameTypesLoading, setGameTypesLoading] = useState(false);

  // Fetch journeys from the API
  const { data: journeys = [], isLoading: journeysLoading } =
    useJourneys(teamId);

  // Combined loading state to prevent flash of content
  const isInitializing =
    categoriesLoading || gameTypesLoading || journeysLoading;

  // Fetch categories for content selection
  const fetchCategories = useCallback(async () => {
    if (!teamId) return;

    setCategoriesLoading(true);
    try {
      const response = await api.get(`/v2/games/categories?team=${teamId}`);
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    } finally {
      setCategoriesLoading(false);
    }
  }, [api, teamId]);

  // Fetch game types for journey creation
  const fetchGameTypes = useCallback(async () => {
    if (!teamId) return;

    setGameTypesLoading(true);
    try {
      const response = await api.get(`/v2/slack/game-types?team=${teamId}`);
      setGameTypes(response.data);
    } catch (error) {
      console.error("Failed to fetch game types:", error);
    } finally {
      setGameTypesLoading(false);
    }
  }, [api, teamId]);

  // Initialize active tab from URL parameter
  useEffect(() => {
    const tabParam = searchParams.get("tab") as TabType;
    if (
      tabParam &&
      (tabParam === "create" ||
        tabParam === "audience" ||
        tabParam === "results")
    ) {
      setActiveTab(tabParam);
    } else if (!searchParams.has("tab")) {
      // Set default tab parameter if not present
      const newParams = new URLSearchParams(searchParams);
      newParams.set("tab", activeTab);
      setSearchParams(newParams);
    }
  }, [searchParams, setSearchParams]);

  // Fetch data when the component mounts or teamId changes
  useEffect(() => {
    fetchCategories();
    fetchGameTypes();
  }, [fetchCategories, fetchGameTypes]);

  // No need for initialization reset anymore as isInitializing is computed from loading states

  // Memoize categories and game types to prevent unnecessary re-renders
  const memoizedCategories = useMemo(() => categories, [categories]);
  const memoizedGameTypes = useMemo(() => gameTypes, [gameTypes]);

  // All journeys are coming from the local context now
  const hasJourneys = journeys.length > 0;

  const handleCreateJourney = (e?: React.MouseEvent) => {
    // Only prevent default if it's a direct click on the journey creation button
    // Don't prevent default for inner form elements like radio buttons, inputs, etc.
    if (e && e.currentTarget === e.target) {
      e.preventDefault();
    }

    // Reset wizard state when creating a new journey
    setWizardState({
      currentStep: 0,
      journeyInfo: {
        name: "",
        description: "",
        touchpointCount: 1,
      },
      contentRows: {},
      touchpoints: [
        {
          game_type_id: "",
          offset_days: 0,
          offset_hours: 9,
          offset_minutes: 0,
          content_selection_type: "surprise_me",
        },
      ],
    });

    setShowJourneyCreationForm(true);
  };

  const handleCancelJourneyCreation = () => {
    // Simple confirmation since we don't track form data outside the wizard anymore
    if (
      window.confirm(
        "Are you sure you want to cancel? Your journey setup will be lost.",
      )
    ) {
      setShowJourneyCreationForm(false);
    }
  };

  const handleJourneyCreated = () => {
    setShowJourneyCreationForm(false);
  };

  const handleViewJourneyDetails = (journeyId: string) => {
    navigate(`/journeys/${journeyId}?team=${teamId}`);
  };

  // Navigate to edit page for a journey
  const handleEditJourney = (journeyId: string) => {
    navigate(`/journeys/edit/${journeyId}?team=${teamId}`);
  };

  // Show a loading state while initializing or fetching data
  if (isInitializing || categoriesLoading || gameTypesLoading) {
    return (
      <div className="container mx-auto px-4 h-full flex items-center justify-center py-12">
        <Spinner />
      </div>
    );
  }

  // Journey templates list (Create tab)
  const JourneyTemplatesList = () => {
    if (showJourneyCreationForm) {
      return (
        <JourneyWizard
          key="journey-wizard"
          categories={memoizedCategories}
          gameTypes={memoizedGameTypes}
          categoriesLoading={categoriesLoading}
          gameTypesLoading={gameTypesLoading}
          wizardState={wizardState}
          onWizardStateChange={setWizardState}
          onCancel={handleCancelJourneyCreation}
          onComplete={handleJourneyCreated}
        />
      );
    }

    if (!hasJourneys) {
      return <EmptyState tab="create" onCreateJourney={handleCreateJourney} />;
    }

    return (
      <div>
        <h3 className="text-lg font-semibold mb-3">Your Journeys</h3>
        <p className="text-gray-600 mb-6">All journeys created are shown</p>

        {/* Grid layout for journey cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
          {journeys.map((journey: Journey) => (
            <div key={journey.id} className="col-span-1">
              <JourneyCard
                journey={journey}
                onClick={() => handleEditJourney(journey.id)}
              />
            </div>
          ))}

          {/* Add button for creating a new journey template */}
          <div className="col-span-1">
            <div
              onClick={handleCreateJourney}
              className="border-2 border-dashed border-gray-300 rounded-xl p-5 h-full flex flex-col items-center justify-center cursor-pointer hover:border-brand hover:bg-brand/10 transition-colors bg-white"
            >
              <div className="w-12 h-12 rounded-full bg-brand/10 flex items-center justify-center mb-3">
                <FontAwesomeIcon icon={faPlus} className="text-brand" />
              </div>
              <h3 className="font-medium text-lg text-gray-800">
                Create a Journey
              </h3>
              <p className="text-gray-600 text-center mt-2">
                Design a new journey with custom touchpoints
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Journey deployments list (Audience tab)
  const JourneyDeploymentsList = () => {
    // Check if we have any journeys to deploy
    if (journeys.length === 0) {
      return (
        <EmptyState
          tab="audience"
          onCreateJourney={() => setActiveTab("create")}
        />
      );
    }

    // In a real implementation, we would fetch deployments separately
    // For now, simulate with journeys data
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-medium text-gray-900">
            Journey Deployments
          </h2>
          <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-brand hover:bg-brandCompliment focus:outline-none">
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Deploy Journey
          </button>
        </div>

        <div className="bg-white shadow overflow-hidden rounded-md">
          <ul className="divide-y divide-gray-200">
            {journeys.map((journey: Journey) => (
              <li key={journey.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center min-w-0 space-x-3">
                    <div className="flex-shrink-0">
                      <FontAwesomeIcon
                        icon={faCalendarAlt}
                        className="text-brand text-lg"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {journey.name}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {journey.channels
                          ?.map((c: PublishedJourneyChannel) => `#${c.name}`)
                          .join(", ") || "No channels"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex flex-col items-end text-sm text-gray-500">
                      <span className="font-medium">Started</span>
                      <span>
                        {new Date(journey.created).toLocaleDateString()}
                      </span>
                    </div>
                    <div>
                      <button className="text-gray-400 hover:text-gray-500">
                        <FontAwesomeIcon icon={faEllipsisV} />
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  // Journey results (Results tab)
  const JourneyResultsList = () => {
    if (!hasJourneys) {
      return <EmptyState tab="results" />;
    }

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-medium text-gray-900">
            Journey Analytics
          </h2>
          <div className="flex space-x-4">
            {/* Filter controls would go here */}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-brand/10 rounded-md p-3">
                  <FontAwesomeIcon
                    icon={faUsers}
                    className="text-brand text-xl"
                  />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Total Participants
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        0
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-brand/10 rounded-md p-3">
                  <FontAwesomeIcon
                    icon={faRoad}
                    className="text-brand text-xl"
                  />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Journey Steps Completed
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        0
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white overflow-hidden shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-brand/10 rounded-md p-3">
                  <FontAwesomeIcon
                    icon={faChartLine}
                    className="text-brand text-xl"
                  />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">
                      Engagement Rate
                    </dt>
                    <dd className="flex items-baseline">
                      <div className="text-2xl font-semibold text-gray-900">
                        0%
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow overflow-hidden rounded-md">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Journey Performance
            </h3>
          </div>
          <ul className="divide-y divide-gray-200">
            {journeys.map((journey: Journey) => (
              <li
                key={journey.id}
                className="px-6 py-4 hover:bg-gray-50 cursor-pointer"
                onClick={() => handleViewJourneyDetails(journey.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center min-w-0 space-x-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {journey.name}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {journey.channels?.length || 0} channels •{" "}
                        {journey.step_count || 0} steps
                      </p>
                    </div>
                  </div>
                  <div>
                    <StatusBadge status={journey.derived_status} />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  // Render active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case "create":
        return <JourneyTemplatesList />;
      case "audience":
        return <JourneyDeploymentsList />;
      case "results":
        return <JourneyResultsList />;
      default:
        return <JourneyTemplatesList />;
    }
  };

  return (
    <div className="container mx-auto">
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-5">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center">
              <FontAwesomeIcon icon={faRoad} className="mr-3 text-brand" />
              Journeys
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Create, deploy, and analyze engagement journeys for your channels
            </p>
          </div>
        </div>
        <Card>{renderTabContent()}</Card>
      </div>
    </div>
  );
}

export default Journeys;
